import { useTranslation } from 'react-i18next'
import * as Switch from '@radix-ui/react-switch'

import { cn } from '@/lib/utils'
import { useSuspiciousBeta } from '@/hooks/useSuspiciousBeta'
import { MY_PUSH_CATEGORIES, myPushChoicesFor, myPushShowsSwitch, nextMyPushValue, type MyPushValue } from '@/lib/myPush'
import type { MyPushChoice } from '@/types'

export interface MyPushPickerProps {
  /** Unique per mounted picker: the radios' group name and the elements' ids. */
  idPrefix: string
  /** What is chosen now. `custom` (read back from the server) checks no choice; null = nothing chosen yet. */
  value: MyPushValue | null
  /** Called with the next choice; never for a click that changes nothing or would untick the last type. */
  onChange: (next: MyPushValue & { mode: MyPushChoice }) => void
  disabled?: boolean
  /** The id of the heading that names the choice (the radio group's accessible name)... */
  labelledBy?: string
  /** ...or the name itself, where no heading id can be given (a Radix dialog title keeps its own id). */
  label?: string
}

/**
 * CP 391 — the "My phone alerts" choices and, under "Humans / Animals / Vehicles", its three ticks (at least one
 * stays ticked: the last one is locked). Controlled and presentational: it saves nothing, so the camera box (which
 * saves on each change) and the group dialog (which previews, then applies) drive the same choices.
 * #406 — the preset "Suspicious + graffiti", and under "All motion" and the ticks the Suspicious switch with its legend.
 * #408 — both for beta users only.
 */
export function MyPushPicker({ idPrefix, value, onChange, disabled = false, labelledBy, label }: MyPushPickerProps) {
  const { t } = useTranslation('settings')
  const beta = useSuspiciousBeta()

  const pick = (action: Parameters<typeof nextMyPushValue>[1]) => {
    if (disabled) return
    const next = nextMyPushValue(value, action)
    if (next) onChange(next)
  }

  return (
    <div role="radiogroup" aria-labelledby={labelledBy} aria-label={labelledBy ? undefined : label} className="divide-y divide-border rounded-lg border border-border">
      {myPushChoicesFor(beta).map((mode) => {
        // #406 — the switch below owns `${idPrefix}-suspicious`, so the preset of that name has its own id
        const id = mode === 'suspicious' ? `${idPrefix}-suspicious-mode` : `${idPrefix}-${mode}`
        const chosen = value?.mode === mode
        return (
          <div key={mode} className="p-3">
            <div className="flex items-start gap-2.5">
              <input
                id={id}
                type="radio"
                name={`${idPrefix}-mode`}
                value={mode}
                checked={chosen}
                onChange={() => pick({ mode })}
                disabled={disabled}
                aria-describedby={`${id}-hint`}
                className="mt-0.5 h-4 w-4 shrink-0 cursor-pointer disabled:cursor-not-allowed"
              />
              <div className="min-w-0 flex-1">
                <label htmlFor={id} className={cn('text-sm font-medium', disabled ? 'cursor-not-allowed' : 'cursor-pointer')}>
                  {t(`myPush.modes.${mode}`)}
                </label>
                <p id={`${id}-hint`} className="text-[11px] text-muted-foreground">{t(`myPush.modeHints.${mode}`)}</p>
              </div>
            </div>

            {mode === 'categories' && chosen && value && (
              <div className="mt-2 pl-6">
                <div className="flex flex-wrap gap-x-4 gap-y-2">
                  {MY_PUSH_CATEGORIES.map((cat) => {
                    const ticked = value.categories.includes(cat)
                    const last = ticked && value.categories.length <= 1
                    return (
                      <label key={cat} htmlFor={`${id}-${cat}`} className="flex items-center gap-2 text-xs">
                        <input
                          id={`${id}-${cat}`}
                          type="checkbox"
                          checked={ticked}
                          onChange={() => pick({ toggle: cat })}
                          disabled={disabled || last}
                          className="h-3.5 w-3.5 shrink-0 rounded border-border"
                        />
                        <span>{t(`myPush.categories.${cat}`)}</span>
                      </label>
                    )
                  })}
                </div>
                {value.categories.length <= 1 && (
                  <p className="mt-1.5 text-[11px] text-muted-foreground">{t('myPush.keepOne')}</p>
                )}
              </div>
            )}

            {beta && chosen && value && myPushShowsSwitch(mode) && (
              <div className="mt-2 flex min-h-11 items-start gap-2.5 pl-6 sm:min-h-0">
                <Switch.Root
                  id={`${idPrefix}-suspicious`}
                  checked={value.suspicious}
                  onCheckedChange={(on) => pick({ suspicious: on })}
                  disabled={disabled}
                  aria-labelledby={`${idPrefix}-suspicious-label`}
                  aria-describedby={`${idPrefix}-suspicious-legend`}
                  className={cn(
                    'relative mt-0.5 inline-flex h-5 w-9 shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent transition-colors',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50',
                    'data-[state=checked]:bg-emerald-500 data-[state=unchecked]:bg-muted-foreground/30',
                  )}
                >
                  <Switch.Thumb className="pointer-events-none block h-3.5 w-3.5 rounded-full bg-white shadow-lg ring-0 transition-transform data-[state=checked]:translate-x-4 data-[state=unchecked]:translate-x-0.5" />
                </Switch.Root>
                <div className="min-w-0 flex-1">
                  <label id={`${idPrefix}-suspicious-label`} htmlFor={`${idPrefix}-suspicious`} className={cn('text-xs font-medium', disabled ? 'cursor-not-allowed' : 'cursor-pointer')}>
                    {t('myPush.suspiciousSwitch')}
                  </label>
                  <p id={`${idPrefix}-suspicious-legend`} className="text-[11px] text-muted-foreground">{t('myPush.suspiciousLegend')}</p>
                </div>
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}
